import * as Sentry from "@sentry/react";
import cx from "classnames";
import { Form, Formik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getProductTypeAsString } from "../../common/shared/enumHelpers";
import { saveAnswer, setCurrentPageProgress } from "../api/ApiAppForm";
import AFLayout from "../components/AFLayout";
import AFLoadingSpinner from "../components/AFLoadingSpinner";
import AFNavigation from "../components/AFNavigation";
import AFSecure from "../components/AFSecure";
import AppFormField from "../components/AppFormField";
import AFImportantDialog from "../components/Modals/AFImportantDialog";
import SetupData from "../constants/SetupData";
import { ControlType } from "../enums/ControlType";
import { PageType } from "../enums/PageType";
import useAFApplication from "../hooks/useAFApplication";
import useAFForm from "../hooks/useAFForm";
import {
    IAnswerInfo,
    IAnswerProps,
    IAppFormQuestion,
    IApplicantInfo,
    IPageInfo
} from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AFSetup.module.scss";
import { getControlType, redirectTo404Page } from "../shared/AppFormHelper";

type AFParams = {
    params: string;
    applicant: string;
};

const DOBID = 5;
const FirstNameID = 7;

const AFSetupPage: React.FC = () => {
    const params = useParams<AFParams>();
    const questionParams = params.params;
    const applicantParams = params.applicant;
    const history = useHistory();

    const [question, setQuestion] = useState<IAppFormQuestion>();
    const [questionReady, setQuestionReady] = useState(false);
    const [formAnswer, setFormAnswer] = useState<IAnswerProps[]>([]);
    const [formReadyToSubmit, setFormReadyToSubmit] = useState(false);
    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [isQuestionReady, setIsQuestionReady] = useState(false);
    const [questionList, setQuestionList] = useState<IAppFormQuestion[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<IAppFormQuestion>();
    const [formAnswerList, setFormAnswerList] = useState<IAnswerProps[]>([]);
    const [questionLabel, setQuestionLabel] = useState("");
    const [firstname, setFirstname] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const bottomRef = useRef(null);
    const [isInErrorState, setIsInErrorState] = useState(false);

    const { actions, selectors } = useContext(AppFormControllerContext);
    const {
        checkFormList,
        checkForm,
        createAnswerList,
        setupPageNavigation,
        setupPageDefaultValue,
        setupPageUpdateValue,
        filterFirstnameLabel,
        filterButtonLabel,
        buttonVisibility
    } = useAFForm();

    const { createApp } = useAFApplication();

    const handleDefaultValue = (question): void => {
        const applicant = selectors.getEachApplicantInfo(applicantParams);
        if (!applicant?.firstName || (!applicant?.isChild && !applicant.email)) {
            setQuestionList([question?.questionList[0]]);
            setQuestionLabel(handleFirstnameLabel(question?.questionList[0]));
            return;
        }
        const questionWithAnswer = question?.questionList?.map((item) => {
            return { ...item, answer: setupPageDefaultValue(item?.id, applicant) };
        });
        const answerList = questionWithAnswer?.reduce((acc, item) => {
            return [
                ...acc,
                {
                    questionId: item.id,
                    disclosure: [],
                    answer: item.answer
                }
            ];
        }, []);
        setFormAnswerList(answerList);
        setQuestionList(questionWithAnswer);
        setQuestionLabel(
            handleFirstnameLabel(questionWithAnswer[question?.questionList?.length - 1])
        );
        setFormReadyToSubmit(true);
    };

    const getQuestion = (): void => {
        const question = SetupData.find((i) => i.params === questionParams);
        if (!question?.id) return;
        const applicant = selectors.getEachApplicantInfo(applicantParams);
        if (applicant?.isChild && question?.id === DOBID) {
            question!.rangeStart = 0;
        }
        if (!applicant || !applicant?.personId) {
            redirectTo404Page();
            return;
        }
        setQuestionLabel(handleFirstnameLabel(question));
        setQuestion({
            ...question,
            questionLabel: handleFirstnameLabel(question),
            answer: setupPageDefaultValue(question?.id, applicant)
        });
        if (question?.questionList?.length) {
            handleDefaultValue(question);
        }
    };

    const handleAnswers = (params: IAnswerProps): void => {
        if (params.questionId === FirstNameID) {
            setFirstname(params?.answer);
        }
        if (question?.questionList?.length) {
            handleMultiAnswerChanges(params);
            return;
        }
        if (!String(params?.answer || "").trim() && questionParams !== "dateofbirth") {
            return;
        }
        const answerParams = [
            {
                questionId: params?.questionId,
                answer: params?.answer || ""
            }
        ];
        setFormReadyToSubmit(checkForm(answerParams));
        const currentApplicant = selectors.getEachApplicantInfo(applicantParams);
        if (
            getControlType(question) === ControlType.RADIOBUTTON &&
            !String(question?.answer || "").trim()
        ) {
            const answerInfo: IAnswerInfo = {
                questionId: answerParams[0].questionId,
                answer: answerParams[0].answer,
                clientNumber: currentApplicant.clientNumber
            }
            saveAnswer(selectors.getQuoteGroupId(), selectors.getApplicationId(), answerInfo);
            handleNextQuestion([params]);
            return;
        }
        handleUpdateInfo({
            ...currentApplicant,
            ...setupPageUpdateValue(question?.params, answerParams)
        });
        setFormAnswer(answerParams);
    };

    const handleMultiAnswerChanges = (answerParam: IAnswerProps): void => {
        if (!questionReady) return;
        const answerList = createAnswerList(questionList, formAnswerList, answerParam);
        setFormAnswerList(answerList);
        const status = checkFormList(questionList, answerList);
        setFormReadyToSubmit(status);
    };

    const handleUpdateInfo = (info): Array<IApplicantInfo> => {
        const list = selectors.getApplicantList();
        const newInfoList = list.reduce((acc, item) => {
            let applicant = item;
            if (item.params === applicantParams) {
                applicant = {
                    ...info
                };
            }
            return [...acc, applicant];
        }, []);
        actions.setApplicantList(newInfoList);
        return newInfoList;
    };

    const handleCreateApplication = (applicants): void => {
        const groupId = selectors.getQuoteGroupId();
        const providerId = selectors.getInsurerId();
        const applicationId = selectors.getApplicationId();
        createApp(
            groupId,
            applicationId,
            providerId,
            applicants
        ).then((res) => {
            const applicants = res?.applicants;
            if (!applicants || !applicants?.length) return;
            actions.setApplicationId(res.applicationId);
            actions.setApplicationToken(res.applicationToken);
            actions.setApplicantList(res?.applicants);
            const pageInfo: IPageInfo = {
                pageType: PageType.AppForm,
                currentClientNumber: 1
            }
            setCurrentPageProgress(res?.applicationId, selectors.getQuoteGroupId(), pageInfo);
            history.push(`/app-form/details/${applicants[0].applicantToken}`);
            setIsLoading(false);
        }).catch((error) => {
            setIsInErrorState(true);
        });
    };

    const handleFinalPage = (applicantList): void => {
        const groupId = selectors.getQuoteGroupId();
        const applicationId = selectors.getApplicationId();
        const currentApplicant = applicantList.find((i) => i.params === applicantParams);
        const nextApplicant = applicantList.find((i) => i.clientNumber === currentApplicant?.clientNumber + 1);
        if (nextApplicant) {
            const pageInfo: IPageInfo = {
                pageType: PageType.Setup,
                currentClientNumber: nextApplicant.clientNumber
            }
            setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo);
            history.push({
                pathname: `/app-form/setup/info/${nextApplicant?.params}`
            });
            return;
        }
        const pageInfo: IPageInfo = {
            pageType: PageType.AppForm,
            currentClientNumber: 1
        }
        setCurrentPageProgress(applicationId, groupId, pageInfo);
        setIsLoading(true);
        handleCreateApplication(applicantList);
    };

    const handleNextQuestion = (answerParams): void => {
        if (isLoading) return;
        setIsQuestionReady(false);
        const applicantList = handleUpdateInfo({
            ...selectors.getEachApplicantInfo(applicantParams),
            ...setupPageUpdateValue(question?.params, answerParams)
        });
        const currentApplicant = applicantList.find((x) => x.params == applicantParams)
        if (question?.isFinal || (question?.isFinalForChild && currentApplicant?.isChild)) {
            handleFinalPage(applicantList);
            return;
        }
        const answerInfo = answerParams?.length && answerParams[0];
        if (question?.params === "info" && answerInfo?.answer === "false") {
            setIsOpenDialog(true);
            return;
        }
        history.push(setupPageNavigation(question?.params, applicantList, applicantParams, true));
    };

    const handleSubmit = (): void => {
        if (!formReadyToSubmit) return;
        setIsQuestionReady(false);
        const currentApplicantClientNumber = selectors.getEachApplicantInfo(applicantParams).clientNumber;
        if (!question?.questionList?.length) {
            const answerInfo: IAnswerInfo = {
                questionId: question?.id,
                answer: formAnswer[0]?.answer,
                clientNumber: currentApplicantClientNumber
            }
            saveAnswer(selectors.getQuoteGroupId(), selectors.getApplicationId(), answerInfo);
            handleNextQuestion(formAnswer);
            return;
        }
        const currentListCount = questionList?.length;

        const answerInfo: IAnswerInfo = {
            questionId: question?.questionList[currentListCount - 1].id,
            answer: formAnswerList[formAnswerList.length - 1]?.answer,
            clientNumber: currentApplicantClientNumber
        }
        saveAnswer(selectors.getQuoteGroupId(), selectors.getApplicationId(), answerInfo);

        if (question?.questionList?.length === currentListCount) {
            handleNextQuestion(formAnswerList);
            return;
        }
        let newList = questionList;
        const currentQuestion = question?.questionList[currentListCount];
        setCurrentQuestion(currentQuestion);
        newList.push(currentQuestion);
        setQuestionLabel(handleFirstnameLabel(currentQuestion));
        setQuestionList(newList);
        setIsQuestionReady(true);
        setFormReadyToSubmit(!currentQuestion?.required || false);
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleFirstnameLabel = (question): string => {
        const info = selectors.getEachApplicantInfo(applicantParams);
        return filterFirstnameLabel(question, {
            ...info,
            firstName: info?.firstName || firstname
        });
    };

    const checkQuestionList = (question): boolean => {
        return !!question?.questionList?.length || false;
    };

    const handleBack = (e): void => {
        e.preventDefault();
        if (isLoading) return;
        history.push(setupPageNavigation(question?.params, selectors.getApplicantList(), applicantParams, false));
    };

    useEffect(() => {
        abTesting.convertAll(20, "", getProductTypeAsString, 0);
        abTesting.convertAll(18, "", getProductTypeAsString, 0);
    }, []);

    useEffect(() => {
        if (!questionParams) return;
        getQuestion();
    }, [questionParams]);

    useEffect(() => {
        if (typeof Sentry !== 'undefined' && isInErrorState) {
            Sentry.captureMessage(`Error occured for ${applicantParams} on the Setup page.`, "error");
        }
    }, [isInErrorState]);

    return (
        <AFLayout isInErrorState={isInErrorState} currentPage={PageType.Setup} applicantParam={params.applicant} >
            <AFImportantDialog
                isOpen={isOpenDialog}
                onClose={(isContinue: boolean) => {
                    const applicantInfo = selectors.getEachApplicantInfo(applicantParams);
                    if (isContinue) {
                        handleUpdateInfo({
                            ...applicantInfo,
                            isChangeGender: true,
                            isChangeSmoked: true
                        });
                        history.push({
                            pathname: `/app-form/setup/gender/${applicantParams}`
                        });
                    }
                    if (!isContinue && !question?.answer) {
                        setQuestion({ ...question, answer: " " });
                        handleUpdateInfo({
                            ...applicantInfo,
                            isInfoUnchange: ""
                        });
                    }
                    setIsOpenDialog(false);
                }}
                clientNumber={selectors.getEachApplicantInfo(applicantParams).clientNumber}
            />
            <div
                className={cx(
                    styles["setup-page"],
                    styles["layout-flex-column"],
                    styles["layout"]
                )}>
                <div className={styles["back-btn-wrapper"]}>
                    <AFSecure />
                    <p className={styles["setup-provider"]}>{selectors.getInsurerName()} insurance application</p>
                </div>
                {isLoading ? (
                    <AFLoadingSpinner />
                ) : (
                    <div
                        className={cx(
                            styles["setup-page-container"],
                            styles["layout-flex-column"]
                        )}>
                        <div
                            className={cx(
                                "layout-flex-column--centered",
                                styles["setup-question-form"]
                            )}>
                            <div
                                className={cx(
                                    "layout-flex-column--centered",
                                    styles["setup-question"]
                                )}>
                                <div className={styles["form-title"]}>
                                    <h1
                                        dangerouslySetInnerHTML={{
                                            __html: question?.mainLabel || ""
                                        }}></h1>
                                </div>

                                <Formik initialValues={{}} onSubmit={handleSubmit}>
                                    {() => (
                                        <Form autoComplete="off"
                                            className={cx(
                                                styles["setup-question-form"]
                                            )}>
                                            <div
                                                className={cx(
                                                    styles["question-form"]
                                                )}>
                                                <label
                                                    className={
                                                        styles["question-form-label"]
                                                    }
                                                    htmlFor={question?.questionLabel}>
                                                    {questionLabel ||
                                                        question?.questionLabel ||
                                                        ""}
                                                </label>
                                                <div
                                                    className={cx([
                                                        styles["question-form-field"]
                                                    ])}>
                                                    {!checkQuestionList(question) && (
                                                        <AppFormField
                                                            question={question}
                                                            answerChanges={handleAnswers}
                                                            fieldReady={setQuestionReady}
                                                        />
                                                    )}
                                                    {checkQuestionList(question) &&
                                                        questionList?.map(
                                                            (question, idx) => (
                                                                <div
                                                                    className={cx(
                                                                        styles[
                                                                        "question-form"
                                                                        ],
                                                                        "layout-flex-column--centered",
                                                                        styles[
                                                                        "question-form-setup"
                                                                        ]
                                                                    )}
                                                                    key={idx}
                                                                    id={
                                                                        question?.questionLabel
                                                                    }>
                                                                    <label
                                                                        className={cx(
                                                                            styles[
                                                                            "question-form-label"
                                                                            ],
                                                                            styles[
                                                                            "question-form-setup"
                                                                            ]
                                                                        )}
                                                                        htmlFor={
                                                                            question?.questionText
                                                                        }
                                                                        dangerouslySetInnerHTML={{
                                                                            __html:
                                                                                question?.questionText ||
                                                                                ""
                                                                        }}></label>

                                                                    <div
                                                                        className={
                                                                            styles[
                                                                            "question-form-field"
                                                                            ]
                                                                        }>
                                                                        <AppFormField
                                                                            question={
                                                                                question
                                                                            }
                                                                            answerChanges={
                                                                                handleAnswers
                                                                            }
                                                                            fieldReady={
                                                                                setQuestionReady
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                </div>
                                            </div>
                                            <AFNavigation
                                                handleBack={handleBack}
                                                skipIsVisible={false}
                                                backIsVisible={questionParams !== "info" || applicantParams !== "applicant1"}
                                                forwardIsVisible={buttonVisibility(
                                                    getControlType(question),
                                                    question?.answer || ""
                                                )}
                                                forwardIsDisabled={!formReadyToSubmit}
                                                forwardType="submit"
                                                forwardText={filterButtonLabel(
                                                    currentQuestion,
                                                    formAnswerList.find(
                                                        (i) =>
                                                            i.questionId === 8
                                                    )?.answer
                                                )}
                                            />
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AFLayout>
    );
};

export default AFSetupPage;
